import { Modification, ModificationAction } from '@core/types';
import { PanelTemplate } from './PanelTemplate';
import { PanelStyles } from './PanelStyles';
import { LoadingManager } from './managers/LoadingManager';
import { sanitizeText, isValidSelector } from '@shared/utils/security';
import { StatusManager } from './managers/StatusManager';
import { UIStateManager } from './managers/UIStateManager';
import { HighlightManager } from './managers/HighlightManager';
import { ImageUploadManager } from './managers/ImageUploadManager';
import { PreviewManager } from './managers/PreviewManager';

export class Panel {
  private element: HTMLElement;
  private onModificationAdd?: (modification: Modification) => void;
  private onModificationRemove?: (index: number) => void;
  private onModificationReorder?: (fromIndex: number, toIndex: number) => void;
  private onSave?: () => void;
  private onClear?: () => void;
  private onClose?: () => void;
  private modifications: Modification[] = [];

  // Managers - initialized in constructor
  private statusManager!: StatusManager;
  private uiStateManager!: UIStateManager;
  private highlightManager!: HighlightManager;
  private imageUploadManager!: ImageUploadManager;
  private previewManager!: PreviewManager;
  private loadingManager!: LoadingManager;

  constructor() {
    this.element = this.createPanel();
    this.initializeManagers();
    this.attachEventListeners();
  }

  private initializeManagers(): void {
    this.statusManager = new StatusManager(this.element);
    this.uiStateManager = new UIStateManager(this.element, this.statusManager);
    this.highlightManager = new HighlightManager(this.element);
    this.imageUploadManager = new ImageUploadManager(this.element);
    this.previewManager = new PreviewManager();
    this.loadingManager = new LoadingManager();

    // Initialize UI state
    this.uiStateManager.updateUIStateForElementSelection(false);
    this.uiStateManager.updateActionFields();
    this.uiStateManager.hideAllFields();

    this.initializePreviewButtonState();
  }

  private initializePreviewButtonState(): void {
    const previewBtn = this.element.querySelector('[data-ce="preview"]') as HTMLElement;
    if (previewBtn && this.previewManager.isEnabled()) {
      previewBtn.classList.add('ce-btn-primary');
    }
  }

  private createPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'ce-panel';
    panel.innerHTML = PanelTemplate.createHTML();

    this.injectStyles();
    document.documentElement.appendChild(panel);
    return panel;
  }

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = PanelStyles.getCSS();
    document.head.appendChild(style);
  }

  private attachEventListeners(): void {
    this.element.addEventListener('click', this.handleClick.bind(this));
    this.element.addEventListener('change', this.handleChange.bind(this));

    // Add drag and drop listeners after the panel is attached to DOM
    setTimeout(() => {
      this.imageUploadManager.setupDragAndDrop();
    }, 100);
  }

  private handleClick(event: Event): void {
    const target = event.target as HTMLElement;
    const action = target.getAttribute('data-ce');
    const itemAction = target.getAttribute('data-ce-item');

    // Handle item-specific actions
    if (itemAction || target.closest('[data-ce-item]')) {
      const itemTarget = target.closest('[data-ce-item]') as HTMLElement;
      const actualItemAction = itemTarget.getAttribute('data-ce-item');

      switch (actualItemAction) {
        case 'remove':
          this.handleRemoveModification(itemTarget);
          break;
        case 'up':
          this.handleMoveModification(itemTarget, -1);
          break;
        case 'down':
          this.handleMoveModification(itemTarget, 1);
          break;
      }
      return;
    }

    // Handle panel-level actions
    const previewTarget = target.closest('[data-ce="preview"]');
    if (previewTarget) {
      event.stopPropagation();
      this.togglePreview();
      return;
    }

    const uploadTarget = target.closest('[data-ce="upload-area"]');
    if (uploadTarget) {
      event.stopPropagation();
      this.imageUploadManager.handleImageUploadClick();
      return;
    }

    const removeImageTarget = target.closest('[data-ce="remove-image"]');
    if (removeImageTarget) {
      event.stopPropagation();
      this.imageUploadManager.handleRemoveImage();
      return;
    }

    switch (action) {
      case 'close':
        event.stopPropagation();
        if (this.onClose) this.onClose();
        break;
      case 'add':
        event.stopPropagation();
        this.handleAddModification();
        break;
      case 'save':
        event.stopPropagation();
        this.handleSave();
        break;
      case 'clear':
        event.stopPropagation();
        if (this.onClear) this.onClear();
        break;
    }
  }

  private handleChange(event: Event): void {
    const target = event.target as HTMLElement;
    const action = target.getAttribute('data-ce');

    if (action === 'action') {
      this.uiStateManager.updateActionFields();
    }

    // Handle file input change
    if (target.id === 'ce-image-input') {
      const fileInput = target as HTMLInputElement;
      const file = fileInput.files?.[0];
      if (file) {
        this.handleImageFileUpload(file);
      }
    }
  }

  private handleAddModification(): void {
    const modification = this.buildModificationFromForm();
    if (modification && this.onModificationAdd) {
      this.onModificationAdd(modification);
      this.uiStateManager.deselectElementAndResetForm();
    }
  }

  private handleRemoveModification(target: HTMLElement): void {
    const index = parseInt(target.getAttribute('data-index') || '-1', 10);
    if (!isNaN(index) && index >= 0 && this.onModificationRemove) {
      this.onModificationRemove(index);
    }
  }

  private handleSave(): void {
    if (this.onSave) {
      this.loadingManager.show('Saving modifications...');

      setTimeout(() => {
        if (this.onSave) {
          this.onSave();
        }
        this.loadingManager.hide();
      }, 1000);
    }
  }

  private handleMoveModification(target: HTMLElement, direction: number): void {
    const index = parseInt(target.getAttribute('data-index') || '-1', 10);
    if (!isNaN(index) && index >= 0 && this.onModificationReorder) {
      const newIndex = index + direction;
      this.onModificationReorder(index, newIndex);
    }
  }

  private togglePreview(): void {
    const isEnabled = this.previewManager.togglePreview();

    // Update UI
    const previewBtn = this.element.querySelector('[data-ce="preview"]') as HTMLElement;
    const previewText = this.element.querySelector('#preview-text') as HTMLElement;

    if (!previewBtn || !previewText) {
      console.warn('Preview button or text element not found');
      return;
    }

    if (isEnabled) {
      previewBtn.classList.add('ce-btn-primary');
      previewText.textContent = 'Preview On';
      this.previewManager.applyPreview(this.modifications);
    } else {
      previewBtn.classList.remove('ce-btn-primary');
      previewText.textContent = 'Preview Off';
      this.previewManager.clearPreview();
    }
  }

  private buildModificationFromForm(): Modification | null {
    const selector = this.getSelectorInput().value.trim();
    if (!selector) return null;

    const action = this.getActionSelect().value as ModificationAction;
    const mod: Modification = { selector, action };

    switch (action) {
      case ModificationAction.SET_TEXT:
        mod.value = this.getValueInput().value;
        break;
      case ModificationAction.REPLACE_IMAGE:
        mod.media_temp_token = this.imageUploadManager.getImageUploadToken();
        mod.value = this.imageUploadManager.getCurrentImageUrl() ?? undefined;
        break;
      case ModificationAction.HIDE:
        // No additional fields
        break;
      default:
        return null;
    }

    return mod;
  }

  private handleImageFileUpload(file: File): Promise<void> {
    // Get session token and page URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionToken = urlParams.get('es') || urlParams.get('editor_session') || '';
    const pageUrl = window.location.href;

    // Clear the file input value to allow subsequent uploads
    const fileInput = document.getElementById('ce-image-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    return this.imageUploadManager
      .handleImageFileUpload(file, sessionToken, pageUrl)
      .then(() => {
        // Upload successful, token and preview are handled by the manager
      })
      .catch((error) => {
        console.error('Image upload error:', error);
        alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      });
  }

  // Helper methods
  private getElement(selector: string): HTMLElement {
    return this.element.querySelector(selector) as HTMLElement;
  }

  private getSelectorInput(): HTMLInputElement {
    return this.getElement('[data-ce="selector"]') as HTMLInputElement;
  }

  getActionSelect(): HTMLSelectElement {
    return this.getElement('[data-ce="action"]') as HTMLSelectElement;
  }

  private getValueInput(): HTMLTextAreaElement {
    return this.getElement('[data-ce="value"]') as HTMLTextAreaElement;
  }

  // Public API
  show(): void {
    this.element.style.display = 'block';
  }

  hide(): void {
    this.highlightManager.clearHighlights();
    this.previewManager.clearPreview();
    this.element.style.display = 'none';
  }

  setStatus(message: string): void {
    this.statusManager.setStatus(message);
  }

  setSessionTimer(expiresAt: string): void {
    this.statusManager.setSessionTimer(expiresAt);
  }

  setPath(path: string): void {
    const safePath = sanitizeText(path);
    const pathElement = this.getElement('[data-ce="path"]');
    const path2Element = this.getElement('[data-ce="path2"]');
    if (pathElement) pathElement.textContent = safePath;
    if (path2Element) path2Element.textContent = safePath;
  }

  setSelector(selector: string): void {
    if (!isValidSelector(selector)) {
      console.warn('Invalid selector provided:', selector);
      return;
    }

    this.getSelectorInput().value = selector;
    this.uiStateManager.updateUIStateForElementSelection(true);
    this.uiStateManager.updateActionFields(); // Add this call
  }

  setValue(value: string): void {
    this.getValueInput().value = value;
  }

  setVersion(version: string): void {
    this.getElement('[data-ce="version"]').textContent = sanitizeText(version);
  }

  updateActionFields(): void {
    this.uiStateManager.updateActionFields();
  }

  renderModifications(modifications: Modification[]): void {
    // Store the modifications array for editing
    this.modifications = modifications;

    // Clear any existing highlights first
    this.highlightManager.clearHighlights();

    // Re-apply preview if it's enabled
    if (this.previewManager.isEnabled()) {
      this.previewManager.applyPreview(modifications);
    }

    const listElement = this.getElement('[data-ce="list"]');

    if (!modifications.length) {
      listElement.innerHTML =
        '<div class="ce-mini" style="opacity:.75">No modifications yet.</div>';
      return;
    }

    listElement.innerHTML = modifications
      .map((mod, index) =>
        PanelTemplate.renderModificationItem(mod, index, this.getActionDisplayName.bind(this)),
      )
      .join('');

    // Setup hover highlights
    this.highlightManager.setupModificationHoverHighlights(modifications);
  }

  private getActionDisplayName(action: ModificationAction): string {
    switch (action) {
      case ModificationAction.SET_TEXT:
        return 'Change text';
      case ModificationAction.HIDE:
        return 'Hide element';
      case ModificationAction.REPLACE_IMAGE:
        return 'Replace image';
      default:
        return action;
    }
  }

  setEnabled(enabled: boolean): void {
    const inputs = this.element.querySelectorAll('input, select, textarea, button');
    inputs.forEach((input) => {
      const element = input as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | HTMLButtonElement;

      element.disabled = !enabled;
      if (element.tagName === 'BUTTON') {
        element.style.opacity = enabled ? '1' : '.6';
        element.style.cursor = enabled ? 'pointer' : 'not-allowed';
      }
    });
  }

  setEnabledWithExitButton(enabled: boolean, disableExit: boolean = false): void {
    const inputs = this.element.querySelectorAll('input, select, textarea, button');
    inputs.forEach((input) => {
      const element = input as
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | HTMLButtonElement;

      // Handle exit button separately based on disableExit flag
      if (element.getAttribute('data-ce') === 'close') {
        element.disabled = disableExit ? !enabled : false;
        element.style.opacity = disableExit && !enabled ? '.6' : '1';
        element.style.cursor = disableExit && !enabled ? 'not-allowed' : 'pointer';
        return;
      }

      element.disabled = !enabled;
      if (element.tagName === 'BUTTON') {
        element.style.opacity = enabled ? '1' : '.6';
        element.style.cursor = enabled ? 'pointer' : 'not-allowed';
      }
    });
  }

  // Event handlers
  on(
    event:
      | 'modificationAdd'
      | 'modificationRemove'
      | 'modificationReorder'
      | 'save'
      | 'clear'
      | 'close',
    callback: any,
  ): void {
    switch (event) {
      case 'modificationAdd':
        this.onModificationAdd = callback;
        break;
      case 'modificationRemove':
        this.onModificationRemove = callback;
        break;
      case 'modificationReorder':
        this.onModificationReorder = callback;
        break;
      case 'save':
        this.onSave = callback;
        break;
      case 'clear':
        this.onClear = callback;
        break;
      case 'close':
        this.onClose = callback;
        break;
    }
  }

  destroy(): void {
    this.statusManager.destroy();
    this.element.remove();
    this.loadingManager.destroy();
  }
}
